/**
 * "Get jobs like this" alert capture on job detail.
 *
 * Measured 2026-10-10 (PostHog, human, 90d): anonymous visitors come back in
 * week 1 at 1.5–2.1%, identified ones at 25–27.5%. Job detail is the biggest
 * Google landing (1,031 sessions/30d, 80% bounce, median 4s) and had no
 * email capture at all.
 *
 * There is no guest-only alert pipeline that actually sends recurring mail:
 * `landing_role_alert_signups` stores intent and sends one acknowledgement,
 * nothing more. Recurring new-job email comes only from `saved_searches`
 * (new-jobs-alerts cron, with a mute link in every send), and those belong to
 * accounts. So the capture reuses the two existing pieces:
 *
 *  - guest: `MagicLinkForm` (Turnstile + verified email — the magic link IS the
 *    double opt-in) with a callbackURL back to this job tagged
 *    `?alert_capture=job_detail`;
 *  - signed in (directly, or on that return trip): `POST /api/saved-searches`
 *    with filters derived from this job's facets and `source: "job_detail"`,
 *    which emits `alert_created { source }` server-side.
 *
 * PostHog flag `jobdetail-alert-capture` (boolean) is the SOLE switch — read
 * client-side so the ISR / edge-cached HTML never varies by visitor. Absent,
 * false or unresolved → nothing renders.
 */

import type { SavedSearchFilters } from "@/lib/db/queries/saved-searches";

export const JOBDETAIL_ALERT_CAPTURE_FLAG = "jobdetail-alert-capture";

/** `source` on `alert_capture_*` and on the server's `alert_created`. */
export const JOBDETAIL_ALERT_SOURCE = "job_detail";

/** Query param stamped on the magic-link callbackURL. Read client-side only. */
export const ALERT_CAPTURE_PARAM = "alert_capture";

export type JobAlertFacets = {
	categorySlug: string | null;
	categoryName: string | null;
	skills: readonly string[];
	seniority: string | null;
};

export type JobAlertTarget = {
	filters: SavedSearchFilters;
	/** Which facet the filters came from — telemetry only. */
	kind: "category" | "skill";
	/** Human label shown to the seeker ("Engineering", "react"). */
	label: string;
};

/**
 * Saved-search filters for "jobs like this one". Category first (one of ~13
 * broad buckets, so the daily alert is never empty for long); the top
 * required skill as a keyword when the job has no known category. `null` when
 * neither exists — then the capture renders nothing rather than promising an
 * "all jobs" alert nobody asked for.
 */
export function buildJobAlertTarget(
	facets: JobAlertFacets,
): JobAlertTarget | null {
	if (facets.categorySlug) {
		return {
			filters: { category: facets.categorySlug },
			kind: "category",
			label: facets.categoryName?.trim() || facets.categorySlug,
		};
	}
	const skill = facets.skills.find(
		(s) => s.trim().length > 0 && s.length <= 80,
	);
	if (skill) {
		return { filters: { q: skill.trim() }, kind: "skill", label: skill.trim() };
	}
	return null;
}
